import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import PortalIdParser from 'PortalIdParser';
import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/components/View';
import { Button } from 'foundations-components/transitional/components/Button';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { H2 } from 'foundations-components/transitional/components/H2';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { Loadable } from 'foundations-components/transitional/utils/Loadable';
import * as AppUninstallableReasons from 'marketplace-ui-apps-core/common/constants/AppUninstallableReasons';
import OAuthUrlInstallButton from 'marketplace-ui-apps-core/details/components/installButton/OAuthUrlInstallButton';
import { useOAuthUrl } from 'marketplace-ui-apps-core/details/hooks/useOAuthUrl';
import { UninstallableMessage } from 'marketplace-ui-apps-core/installability/components/UninstallableMessage';
import { useAppInstallability } from 'marketplace-ui-apps-core/installability/hooks/useAppInstallability';
import { Fragment, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { handleConnectError } from '../../../actions/ConnectActions';
import { useAppContext } from '../../../context/AppContext';
import { useListingView } from '../../../utils/hooks/useListingView';
import { getAppShortlink, getConnectedAppsUrl, getFormattedUrl } from '../../../utils/urls';
import PurchaseMotionButton from './PurchaseMotionButton';

// @ts-expect-error untyped import
import { getEventSelectorId } from '../../../utils/analytics';
// @ts-expect-error untyped import
import { getIsUngatedForUnifiedDetails } from '../../../selectors/auth';
import { getIsHomemadeExternalConnection, getIsHomemadeModalConnection, getIsStandardConnection
// @ts-expect-error untyped import
} from '../../../utils/offerings';
// @ts-expect-error untyped import
import useShortlinkParams from '../../../utils/hooks/useShortlinkParams';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const AsyncRequestPermissionsModal = Loadable({
  loader: () => import(
  // @ts-expect-error untyped import
  /* webpackChunkName: 'request-permissions-modal' */
  './RequestPermissionsModal')
});
const AsyncIntegrationsConnectButton = Loadable({
  loader: () => import( /* webpackChunkName: 'integrations-connect-button' */'ui-addon-integrations-directory-panel/UIIntegrationsConnectButton')
});
const AsyncIntegrationsConnectModal = Loadable({
  loader: () => import( /* webpackChunkName: 'integrations-connect-modal' */'ui-addon-integrations-directory-panel/UIIntegrationsConnectModal')
});
const AsyncDataSyncConnectButton = Loadable({
  loader: () => import( /* webpackChunkName: 'data-sync-connect-button' */'ui-universal-auth/DataSyncAuthButton')
});
const AsyncDashworksAuthButton = Loadable({
  loader: () => import( /* webpackChunkName: 'dashworks-auth-button' */'ui-universal-auth/DashworksAuthButton').then(module => module.DashworksAuthButton)
});
const isEligibleForPurchaseMotion = uninstallableReason => uninstallableReason.label === AppUninstallableReasons.MISSING_HUB_SCOPES && uninstallableReason.missingScopes.includes('sales-navigator-install');
export function ConnectButton({
  appId,
  appName,
  appUrl,
  connectionType,
  features,
  isCustomer,
  isDataSyncApp,
  isDashworksApp,
  isPreview,
  listingId,
  offeringId,
  onConnectButtonClick,
  onTrackEvent,
  onTrackInteraction,
  onTrackPageview
}) {
  const dispatch = useDispatch();
  const appContext = useAppContext();
  const {
    pathname
  } = useLocation();
  const shortlinkParams = useShortlinkParams();
  const isUngatedForUnifiedDetails = useSelector(getIsUngatedForUnifiedDetails);
  const [openIframeModal, setOpenIframeModal] = useState(false);
  const [openPermissionsModal, setOpenPermissionsModal] = useState(false);
  const {
    installabilityLoading,
    uninstallableReason
  } = useAppInstallability({
    appContext,
    offeringId: offeringId !== null && offeringId !== void 0 ? offeringId : 0,
    features
  });
  const {
    appDetails,
    loading: appDetailsLoading
  } = useListingView(isUngatedForUnifiedDetails);
  const standardConnection = getIsStandardConnection(connectionType);
  const homemadeExternalConnection = getIsHomemadeExternalConnection(connectionType);
  const homemadeModalConnection = getIsHomemadeModalConnection(connectionType);

  // Only request OAuth URL if app is Standard Connection without an appUrl
  const oauthUrlConfigured = standardConnection && !appUrl && !isDashworksApp && !isDataSyncApp;
  const {
    oauthUrl,
    oauthUrlLoading
  } = useOAuthUrl({
    offeringId,
    published: !isPreview,
    skip: appContext !== 'app' || !oauthUrlConfigured
  });
  const handleTrackInteraction = useCallback(action => {
    onTrackInteraction(action, {
      section: 'header',
      value: appName
    });
  }, [appName, onTrackInteraction]);
  const onConnectFailed = useCallback(({
    errorType,
    appErrorMessage,
    queryParams = {}
  }) => {
    dispatch(handleConnectError({
      errorType,
      appName,
      queryParams,
      appErrorMessage
    }));
  }, [appName, dispatch]);

  // Public connect button that redirects to portal picker
  if (!isCustomer) {
    return /*#__PURE__*/_jsx(Button, {
      "data-hs-analytics-id": getEventSelectorId('HEADER-CONNECT'),
      use: "primary",
      href: getAppShortlink(pathname, shortlinkParams),
      onClick: onConnectButtonClick,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "marketplace-ui-apps-core.installButton.signIn"
      })
    });
  }

  // Render a loading button if we have not gotten a response from the installablity endpoint yet
  // This prevents vulnerabilities that can be caused by assuming that a user can install an app
  if (installabilityLoading || oauthUrlLoading || appDetailsLoading) {
    return /*#__PURE__*/_jsx(LoadingButton, {
      size: "medium",
      use: "primary",
      disabled: true,
      loading: true,
      role: "button",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "marketplace-ui-apps-core.installButton.install"
      })
    });
  }
  if (!isPreview && uninstallableReason && uninstallableReason.label === AppUninstallableReasons.MISSING_USER_PERMISSIONS) {
    return /*#__PURE__*/_jsxs(Fragment, {
      children: [/*#__PURE__*/_jsx(Button, {
        use: "primary",
        onClick: () => {
          setOpenPermissionsModal(true);
          handleTrackInteraction('click request install app permission button');
        },
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "marketplace-ui-apps-core.installButton.request"
        })
      }), openPermissionsModal && /*#__PURE__*/_jsx(AsyncRequestPermissionsModal, {
        listingId: listingId,
        missingHubUserRoles: uninstallableReason.missingHubUserRoles,
        onClose: () => setOpenPermissionsModal(false),
        onTrackEvent: onTrackEvent,
        onTrackPageview: onTrackPageview
      })]
    });
  }
  const handleConnectionComplete = payload => {
    const redirectUrl = payload && payload.meta && payload.meta.redirectUrl;
    window.location.href = redirectUrl || getConnectedAppsUrl(true);
  };

  // Disabled or Trial button for uninstallable state
  if (!isPreview && uninstallableReason) {
    const toolTip = /*#__PURE__*/_jsx(Tooltip, {
      title: /*#__PURE__*/_jsx(UninstallableMessage, {
        uninstallableReason: uninstallableReason
      }),
      children: /*#__PURE__*/_jsx(Button, {
        "data-hs-analytics-id": getEventSelectorId('HEADER-CONNECT'),
        use: "primary",
        disabled: true,
        role: "button",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "marketplace-ui-apps-core.installButton.install"
        })
      })
    });
    if (uninstallableReason && isEligibleForPurchaseMotion(uninstallableReason)) {
      const purchaseMotionButton = /*#__PURE__*/_jsx(PurchaseMotionButton, {
        fallback: toolTip
      });
      return purchaseMotionButton;
    }
    return toolTip;
  }

  // Button that renders standard InstallWizard for apps with configured OAuth URLs
  if (oauthUrl) {
    return (
      /*#__PURE__*/
      // @ts-expect-error useListingView vs client types
      _jsx(OAuthUrlInstallButton, {
        appDetails: appDetails,
        isPreview: isPreview,
        onClick: onConnectButtonClick,
        onTrackInteraction: handleTrackInteraction
      })
    );
  }

  // Simple button for integrations
  if (standardConnection || homemadeExternalConnection) {
    return /*#__PURE__*/_jsx(Button, {
      "data-hs-analytics-id": getEventSelectorId('HEADER-CONNECT'),
      use: "primary",
      external: standardConnection,
      href: getFormattedUrl(appUrl),
      onClick: onConnectButtonClick,
      role: "button",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "marketplace-ui-apps-core.installButton.install"
      })
    });
  }
  if (isDataSyncApp) {
    return /*#__PURE__*/_jsx(AsyncDataSyncConnectButton, {
      portalId: PortalIdParser.get(),
      appId: appId,
      "data-hs-analytics-id": getEventSelectorId('HEADER-CONNECT'),
      use: "primary",
      onClick: onConnectButtonClick,
      trackingSource: "marketplace",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "marketplace-ui-apps-core.installButton.install"
      })
    });
  }
  if (isDashworksApp) {
    return /*#__PURE__*/_jsx(AsyncDashworksAuthButton, {
      portalId: PortalIdParser.get(),
      appId: appId,
      "data-hs-analytics-id": getEventSelectorId('HEADER-CONNECT'),
      use: "primary",
      onClick: onConnectButtonClick,
      onComplete: __secretId => {
        // Handle successful connection
        handleTrackInteraction('dashworks app connected');
        // Refresh the page to show updated connection status
        window.location.reload();
      },
      onCancel: () => {
        handleTrackInteraction('dashworks app connection cancelled');
      },
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "marketplace-ui-apps-core.installButton.install"
      })
    });
  }

  // Iframe modal button
  if (homemadeModalConnection) {
    return /*#__PURE__*/_jsxs(Fragment, {
      children: [/*#__PURE__*/_jsx(Button, {
        "data-hs-analytics-id": getEventSelectorId('HEADER-CONNECT'),
        use: "primary",
        onClick: () => {
          setOpenIframeModal(true);
          onConnectButtonClick();
        },
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "marketplace-ui-apps-core.installButton.install"
        })
      }), openIframeModal && /*#__PURE__*/_jsxs(Modal, {
        width: 600,
        use: "success",
        children: [/*#__PURE__*/_jsxs(DialogHeader, {
          children: [/*#__PURE__*/_jsx(DialogCloseButton, {
            onClick: () => setOpenIframeModal(false)
          }), /*#__PURE__*/_jsx(H2, {
            "aria-level": 2,
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "marketplace-ui-apps-core.installButton.modalTitle"
            })
          })]
        }), /*#__PURE__*/_jsx(DialogBody, {
          paddingBlockEnd: "400",
          children: /*#__PURE__*/_jsx(AsyncIntegrationsConnectModal, {
            portalId: PortalIdParser.get(),
            appId: appId,
            sourceAppId: "app-marketplace",
            text: I18n.text('marketplace-ui-apps-core.installButton.install'),
            onConnectionComplete: handleConnectionComplete,
            onIntegrationNotFound: () => {
              console.log('not found');
            }
          })
        })]
      })]
    });
  }
  return /*#__PURE__*/_jsx(Fragment, {
    children: /*#__PURE__*/_jsx(View, {
      "data-hs-analytics-id": getEventSelectorId('HEADER-CONNECT'),
      display: "inline-block",
      marginInlineStart: "300",
      children: /*#__PURE__*/_jsx(Flex, {
        alignItems: "center",
        children: /*#__PURE__*/_jsx(AsyncIntegrationsConnectButton, {
          portalId: PortalIdParser.get(),
          appId: appId,
          sourceAppId: "app-marketplace",
          text: I18n.text('marketplace-ui-apps-core.installButton.install'),
          onClick: onConnectButtonClick,
          onConnectionComplete: handleConnectionComplete,
          onConnectionFailed: err => {
            onConnectFailed({
              errorType: err.meta.type,
              appErrorMessage: err.meta.appErrorMessage,
              queryParams: err.meta.queryParams
            });
          }
        })
      })
    })
  });
}